import { Link } from '@tanstack/react-router';
import { DocsLayout } from '../../components/docs/DocsLayout.tsx';
import { MediaVideo } from '../../components/MediaVideo.tsx';
import { useDocumentTitle } from '../../hooks/useDocumentTitle.ts';
import { useT, useRichT } from '../../i18n.ts';

export function DocsChangeManagement() {
  const t = useT();
  const rt = useRichT();
  useDocumentTitle(t('docs.change-management.title'));

  return (
    <DocsLayout
      path="/docs/change-management"
      title={t('docs.change-management.label')}
      lede="Goalpost collects your changes into reviewable batches instead of saving them one at a time. This page explains how a change batch moves from a working draft to an approved version, and how to review, undo, copy, merge, and split drafts along the way."
    >
      <MediaVideo
        base="overview-change-management-software"
        label="A walkthrough of how a change batch moves from a working draft to an approved version in Goalpost"
      />
      <h2 id="intro">Change batches</h2>
      <p>{rt('feature.change-batch.summary')}</p>

      <h2 id="lifecycle">The approval lifecycle</h2>
      <p>A change batch moves through three states:</p>
      <ul>
        <li>
          <strong>Draft:</strong> in progress. Only proposed, so nothing in the
          document has changed yet. You can edit it freely, undo parts
          of it, or discard it.
        </li>
        <li>
          <strong>Pending approval:</strong> submitted and waiting. The change is now
          visible to approvers, and Goalpost is tracking sign-off against the
          document’s{' '}
          <Link to="/docs/approval-rules">approval rule</Link>. It’s still
          not part of the document.
        </li>
        <li>
          <strong>Approved:</strong> official and permanent. The moment the approval
          rule is satisfied, the change is applied and becomes the next{' '}
          <strong>version</strong> of the document. Approved changes form a straight
          line of history, each building on the last.
        </li>
      </ul>
      <p>
        If a document requires no approvals, submitting a draft moves it straight to
        approved. If approvals are required, the change waits. An approver can send
        it back to draft by rejecting it, or you can withdraw it yourself, in both
        cases to revise it before trying again.
      </p>

      <h2 id="history">View history</h2>
      <p>
        Open a document and select <strong>History</strong> from the document nav to
        see each change in turn. By selecting a version, you can see the overall
        state of your document at that time, or the individual changes and
        decisions in that batch. (Drafts you’re still working on live under{' '}
        <strong>Propose Changes</strong>, and submitted changes awaiting sign-off
        under <strong>In Progress</strong>.) Because approved versions are permanent,
        history is a reliable audit trail: you can always answer what the document said
        at the time, and who agreed to it.
      </p>

      <h2 id="draft-checkpoints">Review and undo draft checkpoints</h2>
      <p>
        While you build a draft, Goalpost automatically saves{' '}
        <strong>checkpoints</strong> — snapshots of your work, so you can undo your
        work if you make a mistake (while the change batch is still a draft).
      </p>
      <p>
        To see it, switch to the <strong>Changes</strong> view while editing the
        draft. There you’ll find two things. The draft’s <strong>changes</strong> so
        far list everything you’ve added, replaced, moved, or removed, shown against
        the current version, so you can check exactly what a submission would
        change. Below them, the <strong>Checkpoint history</strong> lists your saves; each
        one is a checkpoint you can <strong>Roll back</strong> to, which returns the
        draft to how it looked at that save. Newer checkpoints stay available to redo
        until you make a new change, at which point they’re discarded.
      </p>
      <p>
        This checkpoint history is a convenience for drafting only, it isn’t preserved
        once the draft is approved: the approved version records what actually
        changed, not the checkpoints you passed through on the way there. For the permanent, approved trail, see{' '}
        <a href="#history">View history</a> above.
      </p>

      <h2 id="copy">Copy a draft</h2>
      <p>
        You can copy some changes from one draft to another. This can be useful if
        you want to work on the same idea in multiple places or directions before
        settling on one approach. Goalpost lets you:
      </p>
      <ul>
        <li>
          <strong>Copy a whole draft to a new draft:</strong> a full duplicate you
          can then take in its own direction.
        </li>
        <li>
          <strong>Copy only selected changes to a new draft:</strong> pull out just
          the parts you want as a fresh, focused proposal.
        </li>
        <li>
          <strong>Copy selected changes into another draft you already have:</strong>{' '}
          combine them with the changes that draft already contains.
        </li>
      </ul>

      <h2 id="merge">Merge drafts</h2>
      <p>
        When two drafts should really go through together, for example because two
        collaborators built related changes separately, you can{' '}
        <strong>merge</strong> them into one. The combined draft contains the changes
        from both, so they’re reviewed and approved as one proposal rather than as
        two that only make sense together. If the drafts touch the same detail in
        conflicting ways, Goalpost surfaces the conflict so you can resolve it before
        submitting.
      </p>

      <h2 id="split">Split into a new draft</h2>
      <p>
        The opposite is just as useful. If a draft has grown to cover two unrelated
        things, you can <strong>split</strong> part of it into a new draft, moving a
        subset of its changes into a proposal of their own. This keeps each proposal
        focused, so reviewers can approve the uncontroversial part now and keep
        discussing the rest, instead of holding up everything for one sticking
        point.
      </p>
      <p>
        Copy, merge, and split all operate on drafts only. Approved versions are
        permanent and are never altered. Together they let you shape the scope of a
        change batch to match how it’s actually going to be reviewed.
      </p>
    </DocsLayout>
  );
}
